import React from 'react'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { CircleInfoIcon } from '@/atoms/Icons/CircleInfoIcon'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsDiv, AsH1, AsH2, HeadingMD, HeadingXS, ParagraphLG, ParagraphMD, ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { Tooltip } from '@/molecules/Tooltip'
import { FaqAccordion } from '@/organisms/FaqAccordion'
import { contentfulFaqLocations, useProjectFaqs } from '@/services/FaqService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { buildLoginUrl } from '@/utils/auth-utils'
import { Translate, useTranslate, TranslateFunction } from '@/utils/translate/translate-client'
import { GuildStatus } from '@/utils/users/usersServer'
import { PromoCard } from '@/views/GuildGiftView/PromoCard'
import { CardHeroGraphic } from './CardHeroGraphic'
import { CtaLinkButton } from './form'
import { mapTierIdToGive } from './tiers'

export function getGuildGiftSteps(t: TranslateFunction) {
  return [
    t(
      'addLovedOnes',
      'Add your loved ones membership for a fraction of the cost, you manage the billing, no hassle for them.',
    ),
    t(
      'weSendInviteLink',
      "We'll send an invite link so they can create their account and get started -- we can even schedule this for later if it's a surprise!",
    ),
    t(
      'theyCanWatchAndVote',
      'They can watch and vote just like you. No shared passwords, no shared watch history, just shared entertainment.',
    ),
  ]
}

export function GuildGiftView(props: { isLoggedIn: boolean; guildStatus: GuildStatus }) {
  const { locale } = useLocale()
  const { t } = useTranslate('guild')
  const track = useSafeTrack()

  const { faqs: guildFaqs, loadingProjectFaqs } = useProjectFaqs({
    locale,
    preview: false,
    projectSlug: slugs.angelStudios,
    location: contentfulFaqLocations.guild,
  })

  const STEPS: string[] = getGuildGiftSteps(t)

  // const QUOTES = [
  //   {
  //     author: 'Patrick W, North Carolina',
  //     body: 'I love that I have been able to pay for my grandmother to watch Angel content. She loves being able to watch Angel movies.',
  //   },
  //   {
  //     author: 'Alice T, Texas',
  //     body: 'I have been able to add my son going to college so he can watch content on his own and save him some money as well',
  //   },
  //   {
  //     author: 'Tony B, Idaho',
  //     body: 'I am so happy to be able to share Angel with our neighbor who’s on hard times, and talk about the movies and shows together',
  //   },
  // ]
  //
  const currency = '$6'
  const frequency = t('month', 'month')
  return (
    <main>
      <section className={'relative overflow-hidden'}>
        <ResponsiveBackgroundImage
          src={getCloudinaryImageUrl({
            path: '/t_Logo Size/v1763574507/angel-studios/guild/gift/Glows.webp',
          })}
          backgroundPosition="bg-center"
          backgroundSize="bg-cover"
          className="pointer-events-none touch-none select-none"
        />
        <div className="relative z-10 m-auto flex w-full max-w-[1350px] flex-col-reverse items-center px-4 pb-8 lg:flex-row lg:justify-around lg:pt-[120px]">
          <div className="max-w-[457px] xl:max-w-[657px]">
            <HeadingXS as={AsH1} weight="bold" color="white" className="lg:photon-display">
              {t('giveAngelToSomeoneYouLove', 'Give Angel to someone you love.')}
            </HeadingXS>
            <ParagraphSM weight="semibold" color="white" className="lg:photon-paragraph-md mt-6 text-balance">
              <Translate
                i18nKey="angelMembershipSeatsForFriends"
                t={t}
                values={{
                  currency,
                  frequency,
                }}
              >
                <span className="md:block">
                  Angel memberships for friends or family at {{ currency }}/{{ frequency }}.
                </span>{' '}
                <span className="text-oxide-bright">Their own password and watch history.</span>
              </Translate>
            </ParagraphSM>

            <div className="mt-12 flex gap-4">
              <GiftMembershipButton isLoggedIn={props.isLoggedIn} guildStatus={props.guildStatus} />
              <LinkButton
                href={paths.external.giftGuild}
                variant="lightened-50"
                onClick={() => track('Guild Gift Giftcard CTA Clicked', { location: 'gift_landing' })}
              >
                {t('giftAGiftCard', 'Gift a Gift Card')}
              </LinkButton>
            </div>
          </div>
          <div className="pb-12 pt-24 md:pt-36 lg:py-0">
            <CardHeroGraphic tier={mapTierIdToGive(props.guildStatus)} />
          </div>
        </div>
        <div
          className="relative h-6 w-full lg:h-[250px]"
          style={{ background: 'linear-gradient(180deg, rgba(20, 20, 20, 0) 0%, rgb(20, 20, 20, 1) 100%)' }}
        />
      </section>

      <div className="relative m-auto max-w-[1170px] px-4 lg:-mt-52">
        <section className="flex w-full flex-col items-center justify-between gap-10 md:flex-row lg:gap-20">
          <div className="flex flex-col gap-4 text-white">
            <HeadingXS weight="bold" as={AsH2} className="lg:photon-heading-md">
              {t('heresWhatYouAreGiving', "Here's What You Are Giving")}
            </HeadingXS>
            <Translate t={t} i18nKey="youCanGiveAccess">
              <ParagraphLG weight="semibold">
                You can give access to Angel movies and shows for <span className="text-oxide-bright">50% off</span>.
              </ParagraphLG>
            </Translate>
          </div>
          <div className="w-fit">
            <PromoCard />
          </div>
        </section>

        <section className="flex flex-col items-center gap-4 pt-16 md:gap-6">
          <HeadingXS color="white" as={AsH2} weight="bold" className="lg:photon-heading-md">
            {t('howItWorks', 'How It Works')}
          </HeadingXS>
          <ol className="flex flex-col gap-6 md:grid md:grid-cols-3">
            {STEPS.map((step, i) => (
              <li key={i}>
                <Well>
                  <HeadingMD as={AsDiv} weight="bold" className="lg:photon-heading-xl" color="core-gray-400">
                    {i + 1}
                  </HeadingMD>
                  <ParagraphMD color="core-gray-400">{step}</ParagraphMD>
                </Well>
              </li>
            ))}
          </ol>
        </section>
        <section className="text-center">
          <div className="py-14">
            <GiftMembershipButton isLoggedIn={props.isLoggedIn} guildStatus={props.guildStatus} />
          </div>
        </section>
        <section className="flex flex-col items-center gap-4 pt-20 md:gap-6">
          <HeadingXS color="white" as={AsH2} weight="bold" className="lg:photon-heading-md">
            {t('whatTheyGet', 'What They Get')}
          </HeadingXS>
          <ul className="flex w-full flex-col gap-6 md:grid md:grid-cols-3">
            <li>
              <Well>
                <ParagraphMD className="text-center" color="core-gray-400">
                  {t('streamAllShowsOnAngel', 'Stream all shows on Angel')}
                </ParagraphMD>
              </Well>
            </li>
            <li>
              <Well>
                <ParagraphMD className="text-center" color="core-gray-400">
                  {t(
                    'limitedAdsOrAdFreeDepending',
                    'Limited Ads or Ad Free depending on which membership plan you have',
                  )}
                </ParagraphMD>
              </Well>
            </li>
            <li>
              <Well>
                <ParagraphMD className="text-center" color="core-gray-400">
                  {t('voteOnMoviesShows', 'Vote on movies and shows that we make next that become Angel Originals')}
                </ParagraphMD>
              </Well>
            </li>
          </ul>
        </section>
        {/*<section className="flex flex-col items-center gap-4 pt-20 md:gap-6">
          <HeadingXS color="white" as={AsH2} weight="bold" className="lg:photon-heading-md">
            {t('whatRealGuildMembersAreSaying', 'What Real Guild Members are Saying')}
          </HeadingXS>
          <ul className="flex w-full flex-col gap-6 md:grid md:grid-cols-3">
            {QUOTES.map((quote, i) => (
              <li key={i}>
                <Well>
                  <ParagraphMD color="core-gray-400" className="italic">
                    &quot;{quote.body}&quot;
                  </ParagraphMD>
                  <ParagraphMD color="core-gray-400">- {quote.author}</ParagraphMD>
                </Well>
              </li>
            ))}
          </ul>
        </section>*/}
        <section className="text-center">
          <div className="py-14">
            <GiftMembershipButton isLoggedIn={props.isLoggedIn} guildStatus={props.guildStatus} />
          </div>
        </section>
        <When condition={(guildFaqs?.length || 0) > 0}>
          <section
            aria-label={t('frequentlyAskedQuestions', 'Frequently Asked Questions')}
            id="faqs"
            key="faqs"
            className="mx-auto scroll-mt-[73px] px-8 py-16 text-left text-white md:max-w-[654px]"
          >
            {!loadingProjectFaqs && <FaqAccordion faqs={guildFaqs} showTopBorder={true} />}
          </section>
        </When>
      </div>
    </main>
  )
}

function Well(props: React.PropsWithChildren) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-start rounded-2xl border border-lightened-200 bg-core-gray-900 p-4 text-white">
      {props.children}
    </div>
  )
}

function GiftMembershipButton(props: { isLoggedIn: boolean; guildStatus: GuildStatus }) {
  const { t } = useTranslate('guild')
  const track = useSafeTrack()

  if (props.guildStatus.isGuildMember)
    return (
      <CtaLinkButton
        href={paths.guild.gift.checkout}
        onClick={() => track('Guild Gift CTA Clicked', { location: 'gift_landing' })}
      >
        {t('giveAMembership', 'Give a Membership')}
      </CtaLinkButton>
    )

  if (props.isLoggedIn)
    return (
      <div className="inline-flex items-center gap-2">
        <CtaLinkButton
          href={paths.guild.join}
          onClick={() => track('Guild Gift Join Guild CTA Clicked', { location: 'gift_landing' })}
        >
          {t('joinToGive', 'Join the Guild to Give')}
        </CtaLinkButton>
        <Tooltip
          isTappable
          content={t(
            'toGiftJoinV1',
            'To give a membership, you too first need to be an Angel Guild member. Follow the link to join, then come back to gift the membership to others.',
          )}
        >
          <CircleInfoIcon size={24} color="core-gray-500" />
        </Tooltip>
      </div>
    )

  return (
    <div className="inline-flex items-center gap-2">
      <CtaLinkButton
        href={buildLoginUrl(paths.guild.gift.index)}
        onClick={() => track('Guild Gift Log In CTA Clicked', { location: 'gift_landing' })}
      >
        {t('loginToGive', 'Log In to Give')}
      </CtaLinkButton>
      <Tooltip
        isTappable
        content={t(
          'toGiftBeAMember',
          'To gift a membership, you too first need to be an Angel Guild member. Please log in to access your existing membership, if any.',
        )}
      >
        <CircleInfoIcon size={24} color="core-gray-500" />
      </Tooltip>
    </div>
  )
}
